import { useEffect, useId, useRef } from "react";
import styled from "@emotion/styled";
import { i18n, mediaQuery } from "src/common";
import { focusRing } from "src/common/style/a11y.style";
import IconModalClose from "../icons/closeModal.icon";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const BaseModalComponent = styled.div({
  padding: 24,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'rgba(50, 50, 50, 0.55)',
  transition: 'all 0.5s ease-in-out',
})

const BaseModalContent = styled.div<{ hasTitle: boolean; maxWidth: number }>(({ hasTitle, maxWidth }) => ({
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  borderRadius: 16,
  maxHeight: '92%',
  width: 'calc(100% - 40px)',
  maxWidth,
  position: 'fixed',
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  padding: 24,
  overflow: 'auto',
  boxShadow: '0 4px 40px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.5s ease-in-out',
  outline: 'none',
  [mediaQuery('tablet')]: {
    padding: hasTitle ? 24 : 48,
  }
}))

const BaseModalCloseButton = styled.button<{ inHeader: boolean }>(({ inHeader }) => ({
  position: inHeader ? 'static' : 'absolute',
  top: 24,
  right: 24,
  margin: inHeader ? '-3px 0' : 0,
  padding: 0,
  background: 'none',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  '&:focus-visible': focusRing,
  [mediaQuery('tablet')]: {
    top: 30,
    right: 30,
  }
}))

// Header matching the CheckoutModal look from the UI library
const BaseModalHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  padding: '8px 0 32px',
  marginBottom: 24,
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: 1,
    background: 'repeating-linear-gradient(90deg, #666666 0 5px, #0000 0 10px)',
  },
})

const BaseModalTitle = styled.h2({
  margin: 0,
  color: '#323232',
  fontSize: 20,
  lineHeight: '24px',
  fontWeight: 700,
})

export type BaseModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  // Accessible name when the modal has no visible title prop
  ariaLabel?: string;
  maxWidth?: number;
  children?: React.ReactNode;
};

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  ariaLabel,
  maxWidth = 610,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (open) {
      const currentOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = currentOverflow;
      };
    }

    return undefined;
  }, [open]);

  // Move focus into the dialog on open and return it to the opener on close
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    contentRef.current?.focus();
    return () => opener?.focus();
  }, [open]);

  if (!open) {
    return null;
  }

  // Keep Tab cycling inside the dialog so the page underneath is never reached
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }

    if (event.key !== 'Tab' || !contentRef.current) return;

    const focusable = Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === contentRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const closeButton = (
    <BaseModalCloseButton type="button" inHeader={!!title} onClick={onClose} aria-label={i18n.t('components.modals.contactModal.closeButton')}>
      <IconModalClose aria-hidden="true" />
    </BaseModalCloseButton>
  );

  return (
    <BaseModalComponent>
      <BaseModalContent
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        hasTitle={!!title}
        maxWidth={maxWidth}
        onKeyDown={handleKeyDown}
      >
        {title ? (
          <BaseModalHeader>
            <BaseModalTitle id={titleId}>{title}</BaseModalTitle>
            {closeButton}
          </BaseModalHeader>
        ) : closeButton}
        {children}
      </BaseModalContent>
    </BaseModalComponent>
  );
};

export default BaseModal;
